import {
  ICompassusCarePlanRepository,
  IPatchCompassusCarePlanRequest,
  CompassusCarePlanApiError,
} from "../../adapters/driven/repositories/compassusCarePlanRepository";

export const CompassusCarePlanServiceImplFactory = ({
  compassusCarePlanRepository,
}: {
  compassusCarePlanRepository: ICompassusCarePlanRepository;
}) => {
  const ok = <T>(data: T) => ({ success: true as const, data });
  const fail = (error: unknown) => ({
    success: false as const,
    error: {
      message: (error as Error).message,
      code: error instanceof CompassusCarePlanApiError ? error.code : undefined,
    },
  });

  const getCarePlan = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const response = await compassusCarePlanRepository.getCarePlanAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      return ok(response.care_plan);
    } catch (error) {
      return fail(error);
    }
  };

  const patchCarePlan = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    body: IPatchCompassusCarePlanRequest;
  }) => {
    try {
      const response = await compassusCarePlanRepository.patchCarePlanAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        body,
      });
      return ok(response.care_plan);
    } catch (error) {
      return fail(error);
    }
  };

  const acquireEditLock = async ({
    clinicId,
    patientId,
    editingSessionId,
  }: {
    clinicId: number;
    patientId: number;
    editingSessionId: string;
  }) => {
    try {
      const response = await compassusCarePlanRepository.acquireEditLockAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        editing_session_id: editingSessionId,
      });
      return ok(response);
    } catch (error) {
      return fail(error);
    }
  };

  const releaseEditLock = async ({
    clinicId,
    patientId,
    editingSessionId,
  }: {
    clinicId: number;
    patientId: number;
    editingSessionId: string;
  }) => {
    try {
      const response = await compassusCarePlanRepository.releaseEditLockAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        editing_session_id: editingSessionId,
      });
      return ok(response);
    } catch (error) {
      return fail(error);
    }
  };

  const getVersions = async ({
    clinicId,
    patientId,
    monthOffset,
    daysBack,
    daysForward,
    excludeDaysBack,
  }: {
    clinicId: number;
    patientId: number;
    monthOffset?: number;
    daysBack?: number;
    daysForward?: number;
    excludeDaysBack?: number;
  }) => {
    try {
      const response = await compassusCarePlanRepository.getVersionsAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        params: {
          month_offset: monthOffset,
          days_back: daysBack,
          days_forward: daysForward,
          exclude_days_back: excludeDaysBack,
        },
      });
      return ok(response.versions);
    } catch (error) {
      return fail(error);
    }
  };

  const getVersion = async ({
    clinicId,
    patientId,
    versionId,
  }: {
    clinicId: number;
    patientId: number;
    versionId: number;
  }) => {
    try {
      const response = await compassusCarePlanRepository.getVersionAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        version_id: versionId,
      });
      return ok(response.version);
    } catch (error) {
      return fail(error);
    }
  };

  const patchVersion = async ({
    clinicId,
    patientId,
    versionId,
    versionName,
  }: {
    clinicId: number;
    patientId: number;
    versionId: number;
    versionName: string | null;
  }) => {
    try {
      const response = await compassusCarePlanRepository.patchVersionAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        version_id: versionId,
        body: { version_name: versionName },
      });
      return ok(response.version);
    } catch (error) {
      return fail(error);
    }
  };

  const getVersionDiff = async ({
    clinicId,
    patientId,
    versionId,
  }: {
    clinicId: number;
    patientId: number;
    versionId: number;
  }) => {
    try {
      const response = await compassusCarePlanRepository.getVersionDiffAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        version_id: versionId,
      });
      return ok(response);
    } catch (error) {
      return fail(error);
    }
  };

  const restoreVersion = async ({
    clinicId,
    patientId,
    versionId,
  }: {
    clinicId: number;
    patientId: number;
    versionId: number;
  }) => {
    try {
      const response = await compassusCarePlanRepository.restoreVersionAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        version_id: versionId,
      });
      return ok(response.care_plan);
    } catch (error) {
      return fail(error);
    }
  };

  const exportCarePlan = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const response = await compassusCarePlanRepository.exportCarePlanAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      return ok(response);
    } catch (error) {
      return fail(error);
    }
  };

  return {
    getCarePlan,
    patchCarePlan,
    acquireEditLock,
    releaseEditLock,
    getVersions,
    getVersion,
    patchVersion,
    getVersionDiff,
    restoreVersion,
    exportCarePlan,
  };
};
